/**
 * JSON → YAML Tool
 *
 * Converts JSON into YAML.
 */

import { stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

type IndentSize = '2' | '4';

export const JsonToYamlTool: ITransformerTool = {
  id: 'json-to-yaml',
  name: 'JSON → YAML',
  description: 'Convert JSON to YAML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['json', 'yaml', 'convert', 'transform'],

  supportedInputTypes: [SEMANTIC_TYPES.JSON],
  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'indentSize',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['2', '4'],
      description: 'YAML indentation',
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'JSON') return false;
    try {
      parseJsonLenient(input);
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseJsonLenient(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid JSON: ${error.message}`, {
        suggestion: 'Check for missing commas, quotes, or brackets',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    const indentSize = (config.indentSize as IndentSize) || '2';
    const indent = parseInt(indentSize, 10);

    return stringifyYaml(parsed, {
      indent,
      lineWidth: 0,
    }).trimEnd();
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
